// frontend/src/services/teamService.js
// Team management (admin + public)
import { supabase } from './supabaseCore';

// ============ Admin Team Operations ============

// Delete a team
export const deleteTeam = async (teamId) => {
    try {
        const { data, error } = await supabase
            .from('teams')
            .delete()
            .eq('id', teamId);

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error deleting team:', error);
        return { data: null, error };
    }
};

// Create a new team
export const createTeam = async (teamData) => {
    try {
        const { data, error } = await supabase
            .from('teams')
            .insert([{
                event_id: teamData.eventId,
                name: teamData.name,
                access_code: teamData.accessCode
            }])
            .select()
            .single();

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error creating team:', error);
        // Check for unique violation on access code
        if (error.code === '23505') {
            return { data: null, error: { message: 'Access code or Team already exists for this event' } };
        }
        return { data: null, error };
    }
};

// Get all teams for an event by ID (for admin UI)
export const getTeamsForEvent = async (eventId) => {
    try {
        const { data, error } = await supabase
            .from('teams')
            .select('*')
            .eq('event_id', eventId)
            .order('name');

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error fetching teams:', error);
        return { data: null, error };
    }
};

// ============ Public Team Operations ============

// Validate access code and return team details
export const validateAccess = async (eventCode, accessCode) => {
    try {
        // 1. Get event details
        const { data: event, error: eventError } = await supabase
            .from('events')
            .select('id, short_name')
            .eq('code', eventCode)
            .single();

        if (eventError || !event) return { isValid: false };

        // 2. Validate team code against DB
        const { data: team, error: teamError } = await supabase
            .from('teams')
            .select('name')
            .eq('event_id', event['id'])
            .ilike('access_code', accessCode) // Case insensitive check
            .single();

        if (teamError || !team) return { isValid: false };

        return {
            isValid: true,
            teamName: team.name,
            eventName: event.short_name // This is the 'dbName' used in event_uploads
        };

    } catch (error) {
        console.error('Error validating access:', error);
        return { isValid: false };
    }
};

// Get players by team name and event
// PUBLIC / anonymous query (TeamPage runs unauthenticated). event_uploads has
// column-level SELECT grants for the `anon` role, so EVERY column selected below
// must be granted to anon or the whole request 401s ("Failed to load players").
// If you add a column here, also run:
//   grant select (<col>) on public.event_uploads to anon;
// See frontend/docs/02_Database_Schema.md → "event_uploads column-level grants".
export const getPlayersByTeam = async (teamName, eventCodeOrName) => {
    try {
        const eventName = eventCodeOrName;

        const { data, error } = await supabase
            .from('event_uploads')
            .select('id, team, event_name, player_name, first_name, last_name, upload_status, photo_url, updated_at, sportradar_photo_url')
            .eq('team', teamName)
            .eq('event_name', eventName)
            .order('player_name');

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error fetching players:', error);
        return { data: null, error };
    }
};

// Get all teams for an event (for admin dashboard / stats)
// This aggregates from event_uploads, so it matches "teams that have players"
export const getTeamsByEvent = async (eventCode) => {
    try {
        // Retrieve event dbName/short_name
        const { data: event } = await supabase
            .from('events')
            .select('short_name')
            .eq('code', eventCode)
            .single();

        const eventName = event?.short_name || eventCode; // Fallback

        const { data, error } = await supabase
            .from('event_uploads')
            .select('team, upload_status, player_name, updated_at')
            .eq('event_name', eventName)
            .order('team');

        if (error) throw error;

        // Group by team and calculate stats
        const teamStats = {};
        data?.forEach(player => {
            if (!teamStats[player.team]) {
                teamStats[player.team] = {
                    team: player.team,
                    total: 0,
                    completed: 0,
                    lastUpdated: null
                };
            }
            teamStats[player.team].total++;
            if (player.upload_status) {
                teamStats[player.team].completed++;
                // Track latest update for this team
                if (player.updated_at) {
                    const currentUpdate = new Date(player.updated_at);
                    if (!teamStats[player.team].lastUpdated || currentUpdate > new Date(teamStats[player.team].lastUpdated)) {
                        teamStats[player.team].lastUpdated = player.updated_at;
                    }
                }
            }
        });

        return {
            data: Object.values(teamStats).sort((a, b) => a.team.localeCompare(b.team)),
            error: null
        };
    } catch (error) {
        console.error('Error fetching teams:', error);
        return { data: null, error };
    }
};

// NOTE: updatePlayerUploadStatus was removed. It set upload_status independently
// of photo_url, which could leave a player flagged "complete" with no photo
// (the progress bar would then disagree with the player cards). It had no
// callers. Always write upload_status together with photo_url.

// Get all events (for event switcher)
export const getAllEvents = async () => {
    try {
        const { data, error } = await supabase
            .from('events')
            .select('code, name, short_name')
            .order('start_date', { ascending: false });

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Error fetching events:', error);
        return { data: null, error };
    }
};
